// The per-subagent activity list, as data.
//
// Folds one observed agent run into the ordered rows the dashboard draws — the
// Claude-Code-shaped "one line per subagent, spinner while it works, ✓ with its
// summary when it reports back". Pure and in its own module for the reason
// `dashboardGroups.js` and `processMonitorGroups.js` are: every rule below is one
// whose regression produces a plausible-looking wrong status rather than an error,
// so it has to be assertable without rendering anything.
//
// It reads three parts of the run summary, and the union is deliberate:
//
//   * `run.modules` — the per-module rows the backend maintains (label, state,
//     summary). The primary source.
//   * `run.parkedTiles` — AUTHORITATIVE for needs-input. See `rowsFor`.
//   * `run.pendingJobs` — a module with a worker job in flight, which also covers
//     a run that started before `modules` existed.

import { tileLabel } from '../panels/tileLabels';
import { serverRunBubble } from '../panels/serverRun';
import { KIND_LABELS } from '../components/agentMonitorRows';

// Reserved key for the orchestrator's own canvas-building row (mirrors the
// backend's `state.MODULE_WORKFLOW_KEY`). Never openable: it is not a module.
export const WORKFLOW_ROW_KEY = '__workflow__';

// The orchestrator names ITSELF, not the product. Reused from the Agents tab's
// vocabulary (`KIND_LABELS`, which mirrors `llm_meter.py`'s kinds) so a run reads
// the same whether you are watching it here or in the process monitor — a third
// naming of the same agent is exactly how "Dennis" ended up labelling a row that
// is really the workflow agent. "Dennis" stays the product's name, in the chat
// header; a row says which AGENT it is: the workflow, or a tile.
export const WORKFLOW_ROW_LABEL = KIND_LABELS.orchestrator;

// A declared step that has not started. Only reachable with a plan — without one
// there is nothing to know about a module before its subagent is activated.
export const PENDING = 'pending';
export const WORKING = 'working';
export const NEEDS_INPUT = 'needs_input';
export const DONE = 'done';
export const FAILED = 'failed';
// Stopped before finishing — a cancelled run, and every module of it that had not
// already reported. It is NOT a flavour of DONE, which is the bug this state exists
// to fix: `cancelled` used to map onto DONE, so a cancelled run drew a green ✓ on its
// "Workflow assistant" row directly under a header pill reading `Cancelled`, and every
// unfinished module row kept the spinner it had when the user stopped it (captioned
// with the live progress of the job that had just been killed).
export const CANCELLED = 'cancelled';
// A run the user HELD. Distinct from PENDING because nothing about it is "not started
// yet" — work is deliberately parked, and often a solve is still running underneath.
export const PAUSED = 'paused';
export const ACTIVITY_STATES = [PENDING, WORKING, NEEDS_INPUT, PAUSED, DONE, FAILED, CANCELLED];

// The backend's own row state for a module whose turn is still on the queue — its
// subagent has not begun. Change-both with `agent_runner/state.MODULE_QUEUED`. It is
// NOT a separate frontend state: it renders as PENDING (see `resolveModule`), because
// "declared but not started" and "queued but not started" look the same to a reader
// and differ only in the caption.
const BACKEND_QUEUED = 'queued';

const CLASS_BY_STATE = {
  [PENDING]: 'dash-activity-pending',
  [WORKING]: 'dash-activity-working',
  [NEEDS_INPUT]: 'dash-activity-needs-input',
  [PAUSED]: 'dash-activity-paused',
  [DONE]: 'dash-activity-done',
  [FAILED]: 'dash-activity-failed',
  [CANCELLED]: 'dash-activity-cancelled',
};

/** The row's CSS class. The colours live in `Dashboard.css`, as
 *  `TileStatusDot` arranges its own. */
export function activityStateClass(state) {
  return CLASS_BY_STATE[state] || CLASS_BY_STATE[WORKING];
}

/** Progress over a plan: how many steps are done, out of how many. */
export function planProgress(rows) {
  const steps = (rows || []).filter((r) => r.isStep);
  return { done: steps.filter((r) => r.state === DONE).length, total: steps.length };
}

const byId = (list, key = 'tileId') =>
  Object.fromEntries((list || []).filter((e) => e && e[key]).map((e) => [e[key], e]));

/**
 * The orchestrator's own row, always first.
 *
 * `detail` reuses `serverRunBubble` verbatim rather than composing its own text,
 * so the dashboard and the in-project ChatPanel can never disagree about what the
 * run is doing — and so the queue depth ("2 modules computing, 3 steps queued")
 * is visible. That last part is the honest answer to sequential turns: several
 * spinners can be showing while only one turn is actually running, and the queue
 * count is what stops stacked work reading as parallel work.
 */
function dennisRow(run, canvasRow) {
  let state = WORKING;
  if (run?.status === 'failed') state = FAILED;
  // NOT `DONE`. A cancelled run drew a green ✓ here, under a header pill that said
  // `Cancelled`, beside a caption ("Slicing is running; I'll wait for the completion
  // message.") describing work the user had just stopped. Three claims, no two of them
  // agreeing — and the only one the reader trusts is the tick.
  else if (run?.status === 'cancelled') state = CANCELLED;
  else if (run?.status === 'paused') state = PAUSED;
  else if (run?.status === 'awaiting_input') state = NEEDS_INPUT;
  else if (run?.status === 'done') state = DONE;

  // `serverRunBubble` is the shared source, so this row and the in-project
  // ChatPanel cannot disagree — and it now answers the terminal states properly,
  // which is what stopped a green checkmark being captioned "Working…".
  let detail = serverRunBubble(run);
  const planned = run?.plan?.steps?.length || 0;
  if (state === WORKING && planned && !canvasRow) {
    // The plan is declared but nothing is built yet, so this row states the
    // workflow agent's OWN assignment. Synthesised from the step count rather than
    // asked of the model: it is derivable, and one less thing for it to get wrong
    // or omit.
    detail = `Add, connect and lay out ${planned} module${planned === 1 ? '' : 's'}`;
  } else if (canvasRow && canvasRow.state === WORKING && canvasRow.tiles != null) {
    // Mid-build: what it has actually put on the canvas so far.
    detail = `Adding and connecting modules — ${canvasRow.tiles} module${
      canvasRow.tiles === 1 ? '' : 's'
    }, ${canvasRow.edges || 0} connection${canvasRow.edges === 1 ? '' : 's'} so far`;
  }
  return {
    key: WORKFLOW_ROW_KEY,
    tileId: null,
    label: WORKFLOW_ROW_LABEL,
    state,
    detail,
    isStep: false,
  };
}

/** Normalise a label for matching a plan step to its module. */
const norm = (s) =>
  String(s || '')
    .trim()
    .toLowerCase();

/**
 * One row per module, resolved from whatever the run reports about it.
 *
 * Shared by the plan path (which supplies the step's own label and `produces`) and
 * the plan-less fallback, so the status precedence below has exactly one home.
 */
function resolveModule(m, { parked, jobs, fallbackDetail = null }) {
  const park = parked[m.tileId];
  // `queued` (the backend derives it from the turn queue) maps onto PENDING: both mean
  // "not started", and PENDING's dashed ring already reads that way, so it needs no
  // new colour. It must be mapped EXPLICITLY rather than passed through — an unknown
  // state falls through `activityStateClass`'s default and would render as working,
  // which is the very confusion the state was added to remove.
  //
  // `cancelled`/`failed` pass STRAIGHT THROUGH, and that is the point of them being
  // real states: the backend settles every unfinished row when a run stops, and a
  // settled row that fell through to `m.state || WORKING` (which is what the old
  // mapping did with any unrecognised value) would keep spinning over a module nothing
  // is working on.
  const state = park
    ? NEEDS_INPUT
    : m.state === BACKEND_QUEUED
      ? PENDING
      : m.state === NEEDS_INPUT
        ? WORKING
        : m.state === CANCELLED || m.state === FAILED
          ? m.state
          : m.state || WORKING;
  // The note is the transient half — the last job outcome, which the backend writes
  // WITHOUT leaving the working state, because a settled job does not settle the
  // module. It wins over the summary while set, so a spinner can say what it is
  // recovering from. `produces` (a plan step's promise) is below both, since
  // anything the module has actually reported beats what it was going to do.
  const detail =
    (park && park.prompt) ||
    m.note ||
    m.summary ||
    detailForJob(jobs[m.tileId]) ||
    fallbackDetail ||
    // Nothing reported yet. Keyed off the STATE rather than a single literal: a
    // finished module captioned "Working…" is worse than one captioned nothing, and
    // a queued one captioned "Working…" is the confusion `queued` exists to fix.
    (state === DONE
      ? 'Done'
      : state === PENDING
        ? 'Waiting to start'
        : state === CANCELLED
          ? 'Stopped'
          : state === FAILED
            ? 'Failed'
            : 'Working…');
  // A job that FAILED leaves a `note` while the row stays `working`, deliberately:
  // the subagent is about to diagnose it and may retry, so `failed` would be a
  // verdict on a module that is still going. But rendering it identically to quiet
  // progress means "a tile broke" reads as "a tile is busy", so the row is flagged
  // for an amber caption while keeping its spinner. Matched on the note the harvest
  // writes (`agent_runner/runner.py`) — change both.
  // Only for a row still SHOWING PROGRESS: once the row itself is `cancelled` or
  // `failed`, its own state carries that and an amber flag on top would double-signal.
  const attention =
    !park &&
    state !== CANCELLED &&
    state !== FAILED &&
    !!m.note &&
    / (failed|cancelled)\b/.test(m.note);
  return {
    state,
    detail,
    attention,
    why: park?.why || null,
    options: park?.options || [],
  };
}

/**
 * The declared plan as rows — the ordered checklist.
 *
 * Each step is bound to its module by LABEL, which is the handle `set_plan`'s
 * schema tells the model to reuse for `create_tile` (and the same by-label handle
 * `graph_tools` already resolves tiles with, so it is not a new thing for it to get
 * wrong). An unbound step is `pending`: declared, not started — which is the whole
 * value of showing a plan before the canvas fills up.
 *
 * A module NOT in the plan still gets a row, appended after the steps. The agent
 * can legitimately add one later, and silently hiding a module that is working
 * would be worse than a checklist that grew.
 */
function planRows(run) {
  const parked = byId(run?.parkedTiles);
  const jobs = byId(run?.pendingJobs);
  const modules = (run?.modules || []).filter((m) => m && m.tileId !== WORKFLOW_ROW_KEY);
  const byLabel = new Map(modules.filter((m) => m.label).map((m) => [norm(m.label), m]));

  // Fallback index by tile TYPE, used only when the label does not match. A step and
  // its module can legitimately carry different labels — the agent renames a tile, or
  // plans "Material" and creates "Material — Steel" — and a step that fails to bind
  // is stuck on `pending` FOREVER while the live module hides among the extras below.
  // The user then sees a module reported complete in the transcript while its plan row
  // still shows what it was going to do. Type is a safe tiebreak because a pipeline
  // normally holds one module per type; where it does not (two meshes), only the first
  // unclaimed one is taken, so no module is ever bound twice.
  const byType = new Map();
  for (const m of modules) {
    if (m.tileType && !byType.has(m.tileType)) byType.set(m.tileType, m);
  }

  const claimed = new Set();
  const steps = (run.plan.steps || []).map((step, i) => {
    let m = byLabel.get(norm(step.module));
    if (!m && step.type) {
      const byT = byType.get(step.type);
      if (byT && !claimed.has(byT.tileId)) m = byT;
    }
    if (m) claimed.add(m.tileId);
    const live = m ? resolveModule(m, { parked, jobs, fallbackDetail: step.produces }) : null;
    return {
      key: m?.tileId || `plan-${i}`,
      tileId: m?.tileId || null,
      label: step.module,
      produces: step.produces || null,
      isStep: true,
      state: live ? live.state : PENDING,
      // A pending step shows its ASSIGNMENT — what that subagent will be asked to
      // do — because "who is going to do what" is the question a plan answers, and
      // `produces` alone reads as a promise with no actor. Once the module is live,
      // what it actually reported wins over either.
      detail: live ? live.detail : step.does || step.produces || 'Not started yet',
      // Kept alongside so a pending row can show the outcome under the assignment.
      does: step.does || null,
      // Who does this step. `user` means the module PARKS for the person rather than
      // running a subagent — so the row is chipped `you`, and its `needs_input` state
      // arrives through `parkedTiles` exactly as a `request_user_input` park does.
      // Anything that is not literally `user` is the agent's, matching the backend's
      // own normalisation: a typo must not silently mark a step as someone else's.
      owner: step.owner === 'user' ? 'user' : 'agent',
      why: live?.why || null,
      options: live?.options || [],
    };
  });

  const extra = modules
    .filter((m) => !claimed.has(m.tileId))
    .sort((a, b) => String(a.startedAt || '').localeCompare(String(b.startedAt || '')))
    .map((m) => ({
      key: m.tileId,
      tileId: m.tileId,
      label: m.label || tileLabel(m.tileType),
      produces: null,
      isStep: true,
      ...resolveModule(m, { parked, jobs }),
    }));

  return [...steps, ...extra];
}

/**
 * One row per module, in activation order.
 *
 * **`parkedTiles` outranks everything.** A tile the run reports as parked renders
 * needs-input whatever its `modules` row says, and a module whose row still says
 * `needs_input` but which is NOT parked renders as working — it has been answered
 * and its subagent has not reported back yet. This is the identical precedence
 * `ProjectPage`'s canvas decorator applies ("parked outranks working, because a
 * module blocked on a person is the one the user must act on"), and keeping it in
 * one pure function is what stops the dashboard and the canvas disagreeing.
 */
function moduleRows(run) {
  const parked = byId(run?.parkedTiles);
  const jobs = byId(run?.pendingJobs);
  const stored = (run?.modules || []).filter((m) => m && m.tileId !== WORKFLOW_ROW_KEY);

  // A run that started before `modules` existed — or one whose row write was lost
  // — still has to show its blocked and busy modules, so synthesise from the two
  // maps that were always there. Without this, an in-flight run at deploy time
  // would render an empty panel, which reads as "nothing is happening".
  const known = new Set(stored.map((m) => m.tileId));
  const synthetic = [...Object.values(parked), ...Object.values(jobs)]
    .filter((e) => !known.has(e.tileId))
    .filter((e, i, all) => all.findIndex((o) => o.tileId === e.tileId) === i)
    .map((e) => ({ tileId: e.tileId, tileType: e.tileType, state: WORKING }));

  return [...stored, ...synthetic]
    .sort((a, b) => String(a.startedAt || '').localeCompare(String(b.startedAt || '')))
    .map((m) => ({
      key: m.tileId,
      tileId: m.tileId,
      label: m.label || tileLabel(m.tileType),
      produces: null,
      isStep: true,
      ...resolveModule(m, { parked, jobs }),
    }));
}

/** `2m 18s`, or `18s` under a minute. Elapsed only — a job has no reliable ETA. */
function elapsed(startedAt) {
  if (!startedAt) return null;
  const started = Date.parse(startedAt);
  if (Number.isNaN(started)) return null;
  const secs = Math.max(0, Math.round((Date.now() - started) / 1000));
  return secs < 60 ? `${secs}s` : `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

/**
 * What a module with a job in flight is actually doing.
 *
 * This used to be the fixed string `Running the {kind} job…`, which is what a
 * 40-minute AM solve showed for its entire duration — nothing on screen moved, so
 * the run read as hung rather than busy. The backend now carries the job record's
 * own `phase`/`progress`/`queuePosition` onto the `pendingJobs` entry (they were
 * being fetched and discarded), and `startedAt` was already on the wire.
 *
 * Assembled from whatever is present, because the fields are per-stage: a queued job
 * has a queue position and no phase, an early one has a phase and no percent.
 */
function detailForJob(job) {
  if (!job) return null;
  const kind = job.kind ? `${job.kind} job` : 'job';
  if (Number.isFinite(job.queuePosition) && job.queuePosition > 0) {
    return `${kind} queued — position ${job.queuePosition}`;
  }
  const bits = [job.phase, Number.isFinite(job.progress) ? `${job.progress}%` : null]
    .filter(Boolean)
    .join(' · ');
  const since = elapsed(job.startedAt);
  return [`${kind}${bits ? `: ${bits}` : ' running'}`, since].filter(Boolean).join(' · ');
}

/**
 * Every row for one run: the workflow agent first, then one row per module.
 *
 * With a declared plan the module rows ARE the plan's steps, in order, including
 * the ones not started — that is the overview. Without one they are derived from
 * whatever the run reports, which is the pre-`set_plan` behaviour and still the
 * path for a run that predates the tool or that never planned.
 */
export function activityRows(run) {
  if (!run) return [];
  const canvasRow = (run.modules || []).find((m) => m?.tileId === WORKFLOW_ROW_KEY);
  const rows = run.plan?.steps?.length ? planRows(run) : moduleRows(run);
  return [dennisRow(run, canvasRow), ...rows];
}

/** The plan's header, or null when the run never declared one. */
export function planHeader(run) {
  const plan = run?.plan;
  if (!plan?.steps?.length) return null;
  return { pipeline: plan.pipeline || 'custom', why: plan.why || null };
}
